import React, { createContext, useContext, ReactNode, useState, useEffect, useMemo, useCallback } from "react"
import { loadStripe, Stripe, StripeElements } from "@stripe/stripe-js"
import { CampingStyleProps, CampProps } from "~/interfaces"
import { stripeOptionsFn } from "~/constants/stripe"
import IInvoice from "~/interfaces/IInvoice"
import useGoogleAnalytics from "~/hooks/useGoogleAnalytics"
import IOrder from "~/interfaces/IOrder"
import { mapLineItemToParams, serviceRateMode } from "~/api2/orders"
import { useCreateOrderPreview } from "~/hooks/useCreateOrderPreview"
import useBuildPriceDetails from "~/components/Invoices/PublicInvoices/useBuildPriceDetails"
import createSubscriptionReservationChannel from "../channels/reservations_channel"
import createSubscriptionLiveWebAppNotificationsChannel from "../channels/live_web_app_notifications_channel"
import useWebsocket from "~/hooks/useWebsocket"

const stripePublicKey = import.meta.env.VITE_STRIPE_PUBKEY

let stripePromise: Promise<Stripe | null> | null = null

type InvoiceStripePaymentMethodType = "card" | "us_bank_account"

export const InvoicesPaymentStep = {
  invoice: "invoice",
  payment: "payment",
  processing: "processing",
}

interface PaymentElementState {
  elementType: string
  collapsed: boolean
  empty: boolean
  complete: boolean
  value: {
    type: InvoiceStripePaymentMethodType
  }
}

export type tInvoicePaymentsStep = (typeof InvoicesPaymentStep)[keyof typeof InvoicesPaymentStep]

interface InvoicesPaymentsContextState {
  camp: CampProps | undefined
  campingStyle: CampingStyleProps | undefined
  step: tInvoicePaymentsStep
  stripePromise: Promise<Stripe | null> | null
  stripeOptions: any
  preview: IOrder | undefined
  isLoading: boolean
  paymentMethod: InvoiceStripePaymentMethodType
  paymentIntentId: string | null
  paymentSuccess: boolean
  isSavingPaymentMethod: boolean
  isAutoCharging: boolean
  handleClickPayOnline: () => void
  handleBackToInvoices: () => void
  handlePaymentTypeChange: (event: any) => void
  handlePayNow: (elements: StripeElements) => void
  setIsSavingPaymentMethod: (value: boolean) => void
  setIsAutoCharging: (value: boolean) => void
  invoice: IInvoice | undefined
}

const initialState: InvoicesPaymentsContextState = {
  camp: undefined,
  campingStyle: undefined,
  step: InvoicesPaymentStep.invoice,
  stripePromise: null,
  stripeOptions: {},
  preview: undefined,
  isLoading: true,
  paymentMethod: "card",
  paymentIntentId: null,
  paymentSuccess: false,
  isSavingPaymentMethod: false,
  isAutoCharging: false,
  handleClickPayOnline: () => { },
  handleBackToInvoices: () => { },
  handlePaymentTypeChange: () => { },
  handlePayNow: () => { },
  setIsSavingPaymentMethod: () => { },
  setIsAutoCharging: () => { },
  invoice: undefined
}

const InvoicesPaymentsContext = createContext<InvoicesPaymentsContextState | undefined>(initialState)

interface InvoicesPaymentsProviderProps {
  invoice: IInvoice
  children: ReactNode
  camp: CampProps
  campingStyle: CampingStyleProps
}

export const InvoicesPaymentsProvider: React.FC<InvoicesPaymentsProviderProps> = ({
  children,
  camp,
  invoice,
  campingStyle,
}) => {
  const { trackForCamp, trackForPark } = useGoogleAnalytics(camp)
  const { data, isPending, mutate } = useCreateOrderPreview()
  const [step, setStep] = useState<tInvoicePaymentsStep>(InvoicesPaymentStep.invoice)
  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const [paymentMethod, setPaymentMethod] = useState<InvoiceStripePaymentMethodType>("card")
  const { upsertIntent } = useBuildPriceDetails(invoice)
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null)
  const [paymentSuccess, setPaymentSuccess] = useState<boolean>(false)
  const [reservationStates, setReservationStates] = useState<any>({})
  const [isSavingPaymentMethod, setIsSavingPaymentMethod] = useState<boolean>(false)
  const [isAutoCharging, setIsAutoCharging] = useState<boolean>(false)

  const [ReservationChannel] = useState(() => createSubscriptionReservationChannel())
  const [LiveWebAppNotificationsChannel] = useState(() => createSubscriptionLiveWebAppNotificationsChannel())

  const handleReservationData = useCallback(
    (received: any) => {
      if (!received?.id) return
      if (received.site?.camp_id !== camp.id) return

      const mReservations = { ...reservationStates }
      const reservationId = received.id

      if (!mReservations[reservationId]) {
        mReservations[reservationId] = { persisted: null, updated: null }
      }

      mReservations[reservationId].persisted = received
      mReservations[reservationId].updated = null

      setReservationStates(mReservations)
    },
    [camp.id, reservationStates],
  )

  const handleNotificationData = useCallback(
    (notification: any) => {
      if (notification.camp_id !== camp.id) return
      if (notification.type !== "payment") return

      const receivedReservation = notification.data?.reservation

      if (!receivedReservation || !receivedReservation.id) return

      const mReservations = { ...reservationStates }
      const reservationId = receivedReservation.id

      if (!mReservations[reservationId]) {
        mReservations[reservationId] = { persisted: null, updated: null }
      }

      mReservations[reservationId].persisted = receivedReservation
      mReservations[reservationId].updated = null

      setReservationStates(mReservations)
    },
    [camp.id, reservationStates],
  )

  useWebsocket(handleReservationData, ReservationChannel, [camp.id, reservationStates])
  useWebsocket(handleNotificationData, LiveWebAppNotificationsChannel, [camp.id, reservationStates])

  useEffect(() => {
    if (!stripePromise && camp.stripe_account_id) {
      stripePromise = loadStripe(stripePublicKey, { stripeAccount: camp.stripe_account_id })
    }
  }, [camp.stripe_account_id])

  useEffect(() => {
    if (!invoice?.reservation?.id) return
    if (step !== InvoicesPaymentStep.processing) return

    const reservationState = reservationStates[invoice.reservation.id]
    if (!reservationState?.persisted) return

    if (reservationState.persisted.status === "paid") {
      setPaymentSuccess(true)

      setTimeout(() => {
        setStep(InvoicesPaymentStep.invoice)
        window.location.reload()
      }, 2000)
    }
  }, [reservationStates, invoice?.reservation?.id, step])

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const paymentIntent = urlParams.get("payment_intent")
    const paymentStatus = urlParams.get("redirect_status")

    if (paymentIntent) {
      setPaymentIntentId(paymentIntent)
    }

    if (paymentStatus === "succeeded" && paymentIntent) {
      setStep(InvoicesPaymentStep.processing)
    }
  }, [])

  const getHashIdFromUrl = () => {
    const pathParts = window.location.pathname.split("/")
    const iIndex = pathParts.findIndex((part) => part === "i")

    if (iIndex !== -1 && pathParts.length > iIndex + 1) {
      return pathParts[iIndex + 1]
    }

    return ""
  }

  const handleClickPayOnline = () => {
    setStep(InvoicesPaymentStep.payment)
  }

  const stripeOptions = useMemo(
    () =>
      invoice.reservation && stripeOptionsFn(invoice.reservation?.total_owed_in_cents, camp, invoice.reservation?.kind, isSavingPaymentMethod && 'off_session' || null),
    [camp, invoice.reservation, isSavingPaymentMethod],
  )

  const handleBackToInvoices = () => {
    setStep(InvoicesPaymentStep.invoice)
  }

  const allDiscounts = [
    ...(camp?.discounts || []),
    ...(campingStyle?.discounts || [])
  ]
  const discountIds = (invoice?.order?.line_items || []).map(li => li.discount_id).filter(Boolean)
  const discount = allDiscounts.find(d => discountIds.includes(d.id))
  const discountSlug = discount?.slug

  const handlePaymentTypeChange = useCallback(
    async (event: PaymentElementState) => {
      mutate({
        order_id: invoice.order_id,
        service_rate_mode: serviceRateMode.payment_link,
        camp_id: camp.id,
        camping_style_id: campingStyle.id,
        is_credit_card_fee_enabled: true,
        discount_slug: discountSlug,
        line_items_attributes: invoice.order.line_items.map((li) => mapLineItemToParams(li, Number(li.tax_rate) > 0)),
        is_service_fee_enabled: true,
        selected_payment_method: event.value.type,
      })
      setPaymentMethod(event.value.type)
    },
    [mutate, invoice.order_id, camp.id, campingStyle.id, invoice.order.line_items, discountSlug],
  )

  const handlePayNow = useCallback(
    async (elements: StripeElements) => {
      trackForCamp("Click Pay Now", { camp_id: camp.id, invoice_id: invoice.id })
      trackForPark("Click Pay Now", { invoice_id: invoice.id })
      setIsProcessing(true)

      const currentHashId = getHashIdFromUrl()

      try {
        if (!stripePromise) {
          console.error("[STRIPE] stripe not loaded")
          return
        }

        const stripe = await stripePromise

        if (!stripe || !elements) {
          console.error("[STRIPE] stripe or elements not available")
          return
        }

        const { clientSecret } = await upsertIntent(paymentMethod, true, isSavingPaymentMethod, isAutoCharging)

        const { error: submitError } = await elements.submit()
        if (submitError) {
          throw new Error(submitError.message)
        }

        const result = await stripe.confirmPayment({
          elements,
          clientSecret,
          confirmParams: {
            return_url: currentHashId && `${window.location.origin}/i/${currentHashId}`,
          },
          redirect: "if_required",
        })

        if (result.paymentIntent?.id) {
          setPaymentIntentId(result.paymentIntent.id)
        }

        const isPaymentInitiated = () => {
          if (!result.paymentIntent) return false

          if (paymentMethod === "card") {
            return result.paymentIntent.status === "succeeded"
          }

          if (paymentMethod === "us_bank_account") {
            return ["processing", "requires_capture", "requires_confirmation", "succeeded"].includes(
              result.paymentIntent.status,
            )
          }

          return false
        }

        if (isPaymentInitiated()) {
          trackForCamp("payment_initiated", {
            value: data?.order?.preview,
            currency: camp.currency,
            payment_method: paymentMethod,
            payment_intent_id: result.paymentIntent?.id,
          })

          setStep(InvoicesPaymentStep.processing)
          setIsProcessing(false)
        } else if (result.error) {
          setIsProcessing(false)
          throw new Error(result.error.message)
        } else {
          setIsProcessing(false)
        }
      } catch (err) {
        setIsProcessing(false)
        console.error("[PAYMENT ERROR]", err)
      }
    },
    [trackForCamp, camp.id, invoice.id, trackForPark, upsertIntent, paymentMethod, camp.currency, data?.order?.preview, isSavingPaymentMethod],
  )

  const values = {
    camp,
    campingStyle,
    step,
    stripePromise,
    stripeOptions,
    preview: data?.order?.preview,
    isLoading: isPending || isProcessing,
    paymentMethod,
    isAutoCharging,
    handleClickPayOnline,
    handleBackToInvoices,
    handlePaymentTypeChange,
    handlePayNow,
    paymentIntentId,
    paymentSuccess,
    invoice,
    isSavingPaymentMethod,
    setIsSavingPaymentMethod,
    setIsAutoCharging,
  }

  return <InvoicesPaymentsContext.Provider value={values}>{children}</InvoicesPaymentsContext.Provider>
}

export const useInvoicesPaymentsContext = () => {
  const context = useContext(InvoicesPaymentsContext)

  if (context === undefined) {
    throw new Error("useInvoicesPaymentsContext must be used within an InvoicesPaymentsProvider")
  }

  return context
}
